import { ITaxonomy } from '../interfaces/ITaxonomy';
import { IPlace } from '../interfaces/IPlace';
import { IStory } from '../interfaces/IStory';
import { ITrail } from '../interfaces/ITrail';
import { PrefsPayload, prefsInitialState } from '../../state/prefsSlice';

const EASY_ACCESS_LABELS = ['fully accessible', 'easier access'];
const TOPIC_MATCH_WEIGHT = 50;
const ACTIVITY_MATCH_WEIGHT = 35;
const DIFFICULTY_IN_RANGE_WEIGHT = 180;
const DIFFICULTY_DISTANCE_PENALTY = 60;

const hasCustomDifficultyPreference = (prefs: PrefsPayload): boolean =>
	(prefs.min_difficulty ?? prefsInitialState.min_difficulty) !==
		prefsInitialState.min_difficulty ||
	(prefs.max_difficulty ?? prefsInitialState.max_difficulty) !==
		prefsInitialState.max_difficulty;

export const hasPreferenceOrderingCriteria = (
	prefs: PrefsPayload,
): boolean => {
	return Boolean(
		(prefs.activities?.length ?? 0) > 0 ||
			(prefs.topics?.length ?? 0) > 0 ||
			hasCustomDifficultyPreference(prefs),
	);
};

const taxonomyKey = (taxonomy: ITaxonomy): string =>
	String(taxonomy.id ?? taxonomy.slug ?? taxonomy.title).toLowerCase();

const buildTaxonomyKeySet = (taxonomy: ITaxonomy[] = []): Set<string> =>
	new Set(taxonomy.map(taxonomyKey));

const preferenceSpecificityMultiplier = (preferredCount: number): number => {
	if (preferredCount <= 1) {
		return 2.5;
	}
	if (preferredCount === 2) {
		return 1.75;
	}
	if (preferredCount === 3) {
		return 1.25;
	}
	return 1;
};

const countMatches = (
	values: ITaxonomy[] = [],
	preferredValues: ITaxonomy[] = [],
): number => {
	if (values.length === 0 || preferredValues.length === 0) {
		return 0;
	}
	const preferredKeys = buildTaxonomyKeySet(preferredValues);
	return values.reduce((count, value) => {
		return count + (preferredKeys.has(taxonomyKey(value)) ? 1 : 0);
	}, 0);
};

export const isEasyAccessTrail = (trail: ITrail): boolean => {
	const difficultyLabel = trail.difficulty_label?.toLowerCase() ?? '';
	if (EASY_ACCESS_LABELS.some((label) => difficultyLabel.includes(label))) {
		return true;
	}
	return typeof trail.difficulty === 'number' && trail.difficulty <= 1;
};

const placeRelatedTrails = (place: IPlace): ITrail[] =>
	(place as IPlace & { trails?: ITrail[] }).trails ?? [];

export const isEasyAccessPlace = (place: IPlace): boolean =>
	placeRelatedTrails(place).some((trail) => isEasyAccessTrail(trail));

export const isEasyAccessStory = (story: IStory): boolean =>
	(story.trails ?? []).some((trail) => isEasyAccessTrail(trail)) ||
	(story.places ?? []).some((place) => isEasyAccessPlace(place));

const trailDifficultyScore = (
	trail: ITrail,
	prefs: PrefsPayload,
): number => {
	if (!hasCustomDifficultyPreference(prefs)) {
		return 0;
	}

	const minDifficulty = prefs.min_difficulty ?? prefsInitialState.min_difficulty ?? 1;
	const maxDifficulty = prefs.max_difficulty ?? prefsInitialState.max_difficulty ?? 6;
	const difficulty = trail.difficulty;

	if (!Number.isFinite(difficulty)) {
		return 0;
	}

	if (difficulty >= minDifficulty && difficulty <= maxDifficulty) {
		return DIFFICULTY_IN_RANGE_WEIGHT;
	}

	const distanceFromRange =
		difficulty < minDifficulty
			? minDifficulty - difficulty
			: difficulty - maxDifficulty;

	return Math.max(
		0,
		DIFFICULTY_IN_RANGE_WEIGHT -
			distanceFromRange * DIFFICULTY_DISTANCE_PENALTY,
	);
};

const trailPreferenceScore = (trail: ITrail, prefs: PrefsPayload): number => {
	const topicSpecificity = preferenceSpecificityMultiplier(
		prefs.topics?.length ?? 0,
	);
	const activitySpecificity = preferenceSpecificityMultiplier(
		prefs.activities?.length ?? 0,
	);
	const topicScore =
		countMatches(trail.topics, prefs.topics) *
		TOPIC_MATCH_WEIGHT *
		topicSpecificity;
	const activityScore =
		countMatches(trail.activities, prefs.activities) *
		ACTIVITY_MATCH_WEIGHT *
		activitySpecificity;
	const difficultyScore = trailDifficultyScore(trail, prefs);

	return topicScore + activityScore + difficultyScore;
};

const placePreferenceScore = (place: IPlace, prefs: PrefsPayload): number => {
	const topicSpecificity = preferenceSpecificityMultiplier(
		prefs.topics?.length ?? 0,
	);
	const topicScore =
		countMatches(place.topics, prefs.topics) *
		TOPIC_MATCH_WEIGHT *
		topicSpecificity;
	const relatedTrails = placeRelatedTrails(place);
	const relatedTrailScores = relatedTrails.map((trail) =>
		trailPreferenceScore(trail, prefs),
	);
	const bestTrailScore =
		relatedTrailScores.length > 0 ? Math.max(...relatedTrailScores) : 0;

	return topicScore + bestTrailScore;
};

const storyPreferenceScore = (story: IStory, prefs: PrefsPayload): number => {
	const topicSpecificity = preferenceSpecificityMultiplier(
		prefs.topics?.length ?? 0,
	);
	const topicScore =
		countMatches(story.topics, prefs.topics) *
		TOPIC_MATCH_WEIGHT *
		topicSpecificity;
	const trailScores = (story.trails ?? []).map((trail) =>
		trailPreferenceScore(trail, prefs),
	);
	const placeScores = (story.places ?? []).map((place) =>
		placePreferenceScore(place, prefs),
	);
	const bestRelatedScore = Math.max(0, ...trailScores, ...placeScores);

	return topicScore + bestRelatedScore;
};

const stablePreferenceSort = <T>(items: T[], getScore: (item: T) => number): T[] => {
	return items
		.map((item, index) => ({
			item,
			index,
			score: getScore(item),
		}))
		.sort((a, b) => {
			if (b.score !== a.score) {
				return b.score - a.score;
			}
			return a.index - b.index;
		})
		.map(({ item }) => item);
};

export const filterTrailsByAccessibility = <T extends ITrail>(
	trails: T[],
	prefs: PrefsPayload,
): T[] => {
	if (!prefs.accessible) {
		return trails;
	}
	return trails.filter((trail) => isEasyAccessTrail(trail));
};

export const filterPlacesByAccessibility = <T extends IPlace>(
	places: T[],
	prefs: PrefsPayload,
): T[] => {
	if (!prefs.accessible) {
		return places;
	}
	return places.filter((place) => isEasyAccessPlace(place));
};

export const filterStoriesByAccessibility = <T extends IStory>(
	stories: T[],
	prefs: PrefsPayload,
): T[] => {
	if (!prefs.accessible) {
		return stories;
	}
	return stories.filter((story) => isEasyAccessStory(story));
};

export const sortTrailsByPreferences = <T extends ITrail>(
	trails: T[],
	prefs: PrefsPayload,
): T[] => {
	if (!hasPreferenceOrderingCriteria(prefs)) {
		return trails;
	}
	return stablePreferenceSort(trails, (trail) => trailPreferenceScore(trail, prefs));
};

export const sortPlacesByPreferences = <T extends IPlace>(
	places: T[],
	prefs: PrefsPayload,
): T[] => {
	if (!hasPreferenceOrderingCriteria(prefs)) {
		return places;
	}
	return stablePreferenceSort(places, (place) => placePreferenceScore(place, prefs));
};

export const sortStoriesByPreferences = <T extends IStory>(
	stories: T[],
	prefs: PrefsPayload,
): T[] => {
	if (!hasPreferenceOrderingCriteria(prefs)) {
		return stories;
	}
	return stablePreferenceSort(stories, (story) => storyPreferenceScore(story, prefs));
};
